export const ERC20_ABI = [
  {
    type: 'function',
    name: 'balanceOf',
    stateMutability: 'view',
    inputs: [ { name: 'account', type: 'address' } ],
    outputs: [ { name: '', type: 'uint256' } ],
  },
] as const;

// CoinMarketCap serves coin logos at this fixed path, keyed by the coin's numeric CMC id.
// 200x200 (vs. the also-available 64x64) so it doesn't look pixelated on the token detail
// page, which renders it up to 120px, including on retina displays.
const cmcLogo = (id: number): string => `https://s2.coinmarketcap.com/static/img/coins/200x200/${ id }.png`;

export const TOKENS = [
  // {
  //   address: '0xF27f345653A9CC97a06eFbB6Ca7A4c44526716Cb',
  //   symbol: 'WBNB',
  //   decimals: 18,
  //   logo: '/assets/BNB.png',
  //   name: 'Wrapped BNB',
  // },
  // {
  //   address: '0x51AF1e727a5a78F98B8c2918cd494ba132D46Eda',
  //   symbol: 'WETH',
  //   decimals: 18,
  //   logo: '/assets/ETH.png',
  //   name: 'Wrapped ETH',
  // },
  {
    address: '0x044b2ED77214aeA517e811f5c80980511F8ff326',
    symbol: 'WDOTO',
    decimals: 18,
    logo: '/assets/logo.svg', // native token, not listed on CoinMarketCap
    name: 'Wrapped DOTO',
  },
  {
    address: '0xAD13394f77AEB4E157d8ec8EcaEF2D77f0894516',
    symbol: 'MOCK',
    decimals: 18,
    logo: '/assets/logo.svg', // test token, not listed on CoinMarketCap
    name: 'Mock Token',
  },
  {
    address: '0x7D4A965754f800EA08B792b00455B31ED1c31fcC',
    symbol: 'WLINK',
    decimals: 18,
    logo: cmcLogo(1975), // Chainlink
    name: 'ChainLink Token (Bridged)',
  },
  {
    address: '0xd68F57d9698088D5e0E6f49E60D1398A849098Fd',
    symbol: 'WBTCB',
    decimals: 8,
    logo: cmcLogo(1), // Bitcoin
    name: 'BTCB Token (Bridged)',
  },
  {
    address: '0x711Dc0Dd81A4cCa64Bc8c3fad4E51D7EeB3082a7',
    symbol: 'WETH',
    decimals: 18,
    logo: cmcLogo(1027), // Ethereum
    name: 'Ethereum Token (Bridged)',
  },
  {
    address: '0x9c4229D652f0D0D7CaDc11554aBFFA8C840798d8',
    symbol: 'WUSDT',
    decimals: 18,
    logo: cmcLogo(825), // Tether
    name: 'Tether USD (Bridged)',
  },
  {
    address: '0xCc42fB0Cf8bc9426E3165eE59dD8c6c19edF13B7',
    symbol: 'WUSDC',
    decimals: 18,
    logo: cmcLogo(3408), // USD Coin
    name: 'USD Coin (Bridged)',
  },
  {
    address: '0xB1e600BE9c665042eCAF99Eb57857E54dA4267d1',
    symbol: 'WDAI',
    decimals: 18,
    logo: cmcLogo(4943), // Dai
    name: 'Dai Token (Bridged)',
  },
  {
    address: '0xd674EC6d370b5b6418679dCbc6A01aaEDec93894',
    symbol: 'WSHIB',
    decimals: 18,
    logo: cmcLogo(5994), // Shiba Inu
    name: 'SHIBA INU (Bridged)',
  },
  {
    address: '0x89e23f72D7Ea5D33D2B6d97Bd63b6c08be7DBc7A',
    symbol: 'WCake',
    decimals: 18,
    logo: cmcLogo(7186), // PancakeSwap
    name: 'PancakeSwap Token (Bridged)',
  },
  {
    address: '0xe415fAEd7f43e22c8Ef0159b33FADF577d94a5f0',
    symbol: 'WBNB',
    decimals: 18,
    logo: cmcLogo(1839), // BNB
    name: 'Wrapped BNB',
  },
] as const;

// Maps a TOKENS `symbol` to the corresponding Chainlink Data Streams Candlestick API symbol.
// Only tokens that represent an asset Chainlink actually tracks have an entry here.
export const TOKEN_SYMBOL_TO_CHAINLINK_SYMBOL: Record<string, string> = {
  WBNB: 'BNBUSD',
  WETH: 'ETHUSD',
  WLINK: 'LINKUSD',
  WBTCB: 'BTCUSD',
  WUSDT: 'USDTUSD',
  WUSDC: 'USDCUSD',
  WDAI: 'DAIUSD',
  WSHIB: 'SHIBUSD',
  WCake: 'CAKEUSD',
};

export const CHAINLINK_SYMBOL_BY_ADDRESS: Record<string, string> = Object.fromEntries(
  TOKENS
    .filter((token) => TOKEN_SYMBOL_TO_CHAINLINK_SYMBOL[token.symbol])
    .map((token) => [ token.address.toLowerCase(), TOKEN_SYMBOL_TO_CHAINLINK_SYMBOL[token.symbol] ]),
);

export interface TokenListMeta {
  readonly name: string;
  readonly symbol: string;
  readonly logo: string;
}

// Lets pages read a token's canonical name/symbol/logo straight from our own TOKENS list
// instead of an external price API, for addresses we curate ourselves.
export const TOKEN_META_BY_ADDRESS: Record<string, TokenListMeta> = Object.fromEntries(
  TOKENS.map((token) => [ token.address.toLowerCase(), { name: token.name, symbol: token.symbol, logo: token.logo } ]),
);

// Tokens whose list-page icon spins continuously, mirroring CoinMarketCap's
// treatment of BTC as its flagship coin — reserved for specific tokens rather
// than applied to every logo, which would just be noisy in a long list.
export const SPINNING_LOGO_ADDRESSES = new Set(
  [ '0xd68F57d9698088D5e0E6f49E60D1398A849098Fd' ].map((address) => address.toLowerCase()),
);

export const myDotContracts = [
  '0xee6429C6Bf6E3974d02B84DdF27A08009a60f770',
  '0x400DAA75481f5cefCCa0F57ccdc9ddE321C6fe7b',
  '0x8517D08b08f79912c9f40a8Ab59E64eC431Db97C',
] as const;
